import React, { useState, useRef, useEffect } from "react";
import { Auth } from "aws-amplify";
import { toast } from "react-toastify";
import { Preloader, Oval } from "react-preloader-icon";
import { captureException } from "@sentry/browser";
import "./common.scss";
import PeekablePasswordInput from "./PeekablePasswordInput.js";
import { sanitizeName } from "../../libs/helpers";
import Tracking from "@magic-engineering/magic-tracking";

const signUpAmplify = async (username, password, first_name, last_name, phone_number) => {
  try {
    await Auth.signUp({
      username,
      password,
      attributes: {
        "custom:first_name": sanitizeName(first_name),
        "custom:last_name": sanitizeName(last_name),
        phone_number,
      },
    });
  } catch (error) {
    captureException(error);
    throw error;
  }
};

export default function SignUp({ setMode, username: initialUsername = "" }) {
  // Redirect to sing-in as it disabled SC-7547
  setMode({ mode: "sign-in" });
  // Only send track event on mount
  useEffect(() => {
    Tracking.visitsSignUp({
      customDestinationPageName_: "SIGN_UP_PG",
    });
  }, []);

  const [username, setUsername] = useState(initialUsername.trim().toLowerCase());
  const [password, setPassword] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const phoneNumberRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [
    verificationSent,
    // temporary: removed due to #214 enable clients to signup w/o email verification link
    // setVerificationSent
  ] = useState(false);

  const handleUsernameChange = (event) => {
    setUsername(event.target.value.trim().toLowerCase());
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const handleFirstNameChange = (event) => {
    setFirstName(event.target.value);
  };

  const handleLastNameChange = (event) => {
    setLastName(event.target.value);
  };

  const handlePhoneNumberChange = (event) => {
    phoneNumberRef.current.setCustomValidity("");
    setPhoneNumber(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsLoading(true);
    try {
      const formattedPhoneNumber = `+${phoneNumber.replace(/\D+/g, "")}`;
      if (!formattedPhoneNumber || formattedPhoneNumber === "+") {
        phoneNumberRef.current.setCustomValidity("Invalid phone number!");
        throw new Error("Invalid phone number!");
      }
      setPhoneNumber(formattedPhoneNumber);
      phoneNumberRef.current.value = formattedPhoneNumber;

      await signUpAmplify(username, password, firstName, lastName, formattedPhoneNumber);
      //temporary: remove due to #214 enable clients to signup w/o email verification link
      // setVerificationSent(true);
      toast.success("Sign up successful!");
      setMode({ mode: "sign-in", username });
    } catch (error) {
      toast.error(error.message);
    }
    setIsLoading(false);
  };

  return (
    <div className="container" data-testid="sign-up">
      <div className="row">
        <div className="col"></div>
        {verificationSent ? (
          <div className="col sign-in-container">
            <div className="pb-2 header">Verification email sent!</div>
            <div className="pt-3 pb-3 new-user">
              Please check your email inbox and follow the instructions. Email sent to {username}
            </div>
            <div className="pt-3">
              <button
                type="submit"
                className="float-right"
                onClick={() => setMode({ mode: "sign-in", username })}
              >
                CONTINUE
              </button>
            </div>
          </div>
        ) : (
          <form className="col sign-in-container" onSubmit={handleSubmit}>
            <div className="pb-2 header">Sign up</div>
            <div className="pt-3 pb-3 new-user">
              Already a user?
              <span
                className="ml-3"
                onClick={() => {
                  Tracking.clicksLoginNow();
                  setMode({ mode: "sign-in", username });
                }}
              >
                Login now
              </span>
            </div>
            <div className="col pt-2 pb-3 input-field">
              <label className="row">
                <span className="mt-1 mb-1">First name</span>
                <input
                  type="text"
                  className="form-control mt-2"
                  placeholder="Jane"
                  onChange={handleFirstNameChange}
                  required
                  data-testid="first-name"
                />
              </label>
            </div>
            <div className="col pt-2 pb-3 input-field">
              <label className="row">
                <span className="mt-1 mb-1">Last name</span>
                <input
                  type="text"
                  className="form-control mt-2"
                  placeholder="Doe"
                  onChange={handleLastNameChange}
                  required
                  data-testid="last-name"
                />
              </label>
            </div>
            <div className="col pt-2 pb-3 input-field">
              <label className="row">
                <span className="mt-1 mb-1">Email</span>
                <input
                  type="email"
                  className="form-control mt-2"
                  placeholder="jane@mycompany.com"
                  onChange={handleUsernameChange}
                  defaultValue={username}
                  required
                  data-testid="email"
                />
              </label>
            </div>
            <div className="col pt-2 pb-3 input-field">
              <label className="row">
                <span className="mt-1 mb-1">Password</span>
                <PeekablePasswordInput
                  className="form-control mt-2"
                  placeholder="**********"
                  onChange={handlePasswordChange}
                  required
                  data-testid="password"
                />
              </label>
            </div>
            <div className="col pt-2 pb-3 input-field">
              <label className="row">
                <span className="mt-1 mb-1">Phone Number</span>
                <input
                  ref={phoneNumberRef}
                  type="tel"
                  className="form-control mt-2"
                  placeholder="+1 (123) 456-7890"
                  onChange={handlePhoneNumberChange}
                  required
                  data-testid="phone-number"
                />
              </label>
            </div>
            <div className="pt-3">
              {isLoading ? (
                <Preloader
                  use={Oval}
                  size={45}
                  strokeWidth={6}
                  strokeColor="#262626"
                  duration={2000}
                  className="float-right"
                />
              ) : (
                <button type="submit" className="float-right">
                  CREATE ACCOUNT
                </button>
              )}
            </div>
          </form>
        )}
        <div className="col"></div>
      </div>
    </div>
  );
}
